"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { CircleCheck, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
const feeData = [{
  id: 1,
  year: "Litet badrum",
  semester: "UPP TILL 5 M²",
  tuition: "Budgetnivå",
  hostel: "Standard",
  mess: "Premium",
  rowSpan: 2,
  isFirstOfBlock: true,
  bgClass: "bg-muted/30"
}, {
  id: 2,
  year: "",
  semester: "ARBETSKOSTNAD",
  tuition: "65.000 kr",
  hostel: "85.000 kr",
  mess: "110.000 kr",
  rowSpan: 0,
  isFirstOfBlock: false,
  bgClass: "bg-muted/30"
}, {
  id: 3,
  year: "Mellanbadrum",
  semester: "5 - 10 M²",
  tuition: "Budgetnivå",
  hostel: "Standard",
  mess: "Premium",
  rowSpan: 2,
  isFirstOfBlock: true,
  bgClass: "bg-background"
}, {
  id: 4,
  year: "",
  semester: "ARBETSKOSTNAD",
  tuition: "85.000 kr",
  hostel: "115.000 kr",
  mess: "145.000 kr",
  rowSpan: 0,
  isFirstOfBlock: false,
  bgClass: "bg-background"
}, {
  id: 5,
  year: "Stort badrum",
  semester: "ÖVER 10 M²",
  tuition: "Budgetnivå",
  hostel: "Standard",
  mess: "Premium",
  rowSpan: 2,
  isFirstOfBlock: true,
  bgClass: "bg-muted/30"
}, {
  id: 6,
  year: "",
  semester: "ARBETSKOSTNAD",
  tuition: "110.000 kr",
  hostel: "140.000 kr",
  mess: "180.000 kr",
  rowSpan: 0,
  isFirstOfBlock: false,
  bgClass: "bg-muted/30"
}, {
  id: 7,
  year: "Våtrumstyp",
  semester: "GOLVBRUNN",
  tuition: "Byte ingår",
  hostel: "Flytt ingår",
  mess: "Special",
  rowSpan: 2,
  isFirstOfBlock: true,
  bgClass: "bg-background"
}, {
  id: 8,
  year: "",
  semester: "TÄTSKIKT",
  tuition: "Folie",
  hostel: "Folie+",
  mess: "Premium",
  rowSpan: 0,
  isFirstOfBlock: false,
  bgClass: "bg-background"
}, {
  id: 9,
  year: "El & Värme",
  semester: "GOLVVÄRME",
  tuition: "El-slinga",
  hostel: "Vattenburen",
  mess: "Kombinerad",
  rowSpan: 2,
  isFirstOfBlock: true,
  bgClass: "bg-muted/30"
}, {
  id: 10,
  year: "",
  semester: "BELYSNING",
  tuition: "Spotlights",
  hostel: "Dimmer",
  mess: "Smart-styrd",
  rowSpan: 0,
  isFirstOfBlock: false,
  bgClass: "bg-muted/30"
}, {
  id: 11,
  year: "Inredning",
  semester: "MONTERING",
  tuition: "Standard",
  hostel: "Design",
  mess: "Platsbyggt",
  rowSpan: 1,
  isFirstOfBlock: true,
  bgClass: "bg-background"
}];
const requirementsData = [{
  id: "doc-planning",
  type: "doc",
  text: "Önskemål om planlösning och eventuella förändringar"
}, {
  id: 1,
  type: "entry",
  text: "Badrummets totala yta och takhöjd"
}, {
  id: 2,
  type: "entry",
  text: "Val av material (kakel, klinker, natursten)"
}, {
  id: 3,
  type: "entry",
  text: "Eventuell flytt av rördragning och avlopp"
}, {
  id: 4,
  type: "entry",
  text: "Installation av golvvärme och ny el"
}, {
  id: 6,
  type: "doc",
  text: "Ritningar och mått på befintligt badrum"
}, {
  id: 7,
  type: "doc",
  text: "Önskemål om inredning och armaturer"
}, {
  id: 8,
  type: "doc",
  text: "Information om fastighetens ålder"
}, {
  id: 9,
  type: "doc",
  text: "Bilder på nuvarande röranslutningar"
}];
const termsData = [{
  id: 1,
  category: "Betalningsplan",
  text: "Vi tillämpar en trygg betalningsmodell där fakturering sker i etapper efter godkända moment."
}, {
  id: 2,
  category: "Betalningsplan",
  text: "Slutfaktura skickas först när hela entreprenaden är besiktigad och godkänd av kund."
}, {
  id: 3,
  category: "Betalningsplan",
  text: "Materialkostnader för inredning faktureras vanligtvis vid beställning för att säkra leveranstider."
}, {
  id: 4,
  category: "Betalningsplan",
  text: "ROT-avdraget administreras direkt av oss för din bekvämlighet och dras av på arbetskostnaden."
}, {
  id: 5,
  category: "Garantier & Trygghet:",
  text: "Vi lämnar alltid 10 års garanti på tätskikt och utfört arbete enligt branschregler."
}, {
  id: 6,
  category: "Garantier & Trygghet:",
  text: "Full ansvarsförsäkring finns under hela byggtiden för din och din fastighets säkerhet."
}, {
  id: 7,
  category: "Avbokningsregler:",
  text: "Eventuella ändringar i projektplanen ska ske skriftligen. Vi följer Konsumenttjänstlagens riktlinjer för avbeställning."
}];
const partnersData = [{
  id: 1,
  name: "BKR",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626429/img/bkr_logo.png"
}, {
  id: 2,
  name: "GVK",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626429/img/gvk_logo.png"
}, {
  id: 3,
  name: "Säker Vatten",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626429/img/sakervatten_logo.png"
}, {
  id: 4,
  name: "Elsäkerhetsverket",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626429/img/elsakerhet_logo.png"
}, {
  id: 5,
  name: "Byggkeramikrådet",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626429/img/byggkeramik_logo.png"
}, {
  id: 6,
  name: "Trygg Hansa",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626429/img/trygghansa_logo.png"
}];
export default function Swedishpricingfactors() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: tableRef,
    isVisible: isTableVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardsRef,
    isVisible: isCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: termsRef,
    isVisible: isTermsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const entryReqs = requirementsData.filter(req => req.type === "entry");
  const docReqs = requirementsData.filter(req => req.type === "doc");
  const groupedTerms = termsData.reduce((acc, term) => {
    if (!acc[term.category]) {
      acc[term.category] = [];
    }
    acc[term.category].push(term);
    return acc;
  }, {} as Record<string, typeof termsData>);
  return <section data-section-id="257980" id="swedishpricingfactors" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        
        {}
        <div ref={headerRef} className={cn("flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6 transition-all duration-700 opacity-0 translate-y-8", isHeaderVisible && "opacity-100 translate-y-0")}>
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground font-default tracking-tight mb-2">Vad kostar det att renovera badrum i Karlstad?</h2>
            <p className="text-muted-foreground font-medium uppercase tracking-wide !text-sm">Kostnaden för att renovera badrum i Karlstad beror bland annat på badrummets storlek, materialval, installationer och renoveringens omfattning. Varje projekt har olika förutsättningar och kan omfatta rivning, VVS, el, tätskikt, plattsättning samt montering av inredning. Efter ett kostnadsfritt hembesök går vi igenom badrummet och dina önskemål innan vi tar fram en tydlig och specificerad offert.</p>
          </div>
          <div className="shrink-0">
            <Link to="/">
              <img src="/img/proffsbadrum_logo.png" data-wvc-srcset="/img/proffsbadrum_logo.png-wvc-srcset" data-wvc-sizes="/img/proffsbadrum_logo.png-wvc-sizes" alt="PROFFSBADRUM.SE" className="h-12 w-auto object-contain" loading="lazy" />
            </Link>
          </div>
        </div>

        {}
        <div ref={tableRef} className={cn("mb-8 transition-all duration-700 delay-100 opacity-0 translate-y-8", isTableVisible && "opacity-100 translate-y-0")}>
          <div className="rounded-lg overflow-hidden border border-border shadow-md">
            <Table>
              <TableHeader className="bg-foreground hover:bg-foreground">
                <TableRow className="hover:bg-foreground border-none">
                  <TableHead className="text-background font-bold text-center py-4 h-auto font-default">KATEGORI</TableHead>
                  <TableHead className="text-background font-bold text-center py-4 h-auto font-default">OMFATTNING</TableHead>
                  <TableHead className="text-background font-bold text-center py-4 h-auto font-default">BUDGET</TableHead>
                  <TableHead className="text-background font-bold text-center py-4 h-auto font-default">STANDARD</TableHead>
                  <TableHead className="text-background font-bold text-center py-4 h-auto font-default">PREMIUM</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feeData.map((row, i) => <TableRow key={row.id} data-index={i} className={cn("border-b border-border/50 transition-colors", row.bgClass, `hover:${row.bgClass}`)}>
                    {row.isFirstOfBlock && <TableCell rowSpan={row.rowSpan} className="text-center font-semibold text-foreground align-middle border-r border-border/20 font-default">
                        {row.year}
                      </TableCell>}
                    <TableCell className="text-center py-3 font-sans text-muted-foreground">{row.semester}</TableCell>
                    <TableCell className="text-center py-3 font-medium text-foreground">{row.tuition}</TableCell>
                    <TableCell className="text-center py-3 text-foreground">{row.hostel}</TableCell>
                    <TableCell className="text-center py-3 text-foreground">{row.mess}</TableCell>
                  </TableRow>)}
                {}
                <TableRow className="bg-primary hover:bg-primary border-none">
                  <TableCell colSpan={5} className="text-center text-primary-foreground font-bold py-4 text-sm md:text-base tracking-wide font-default">
                    FAST PRIS PÅ ARBETE | TRYGGHETSPAKET INGÅR ALLTID | ROT-AVDRAG HANTERAS AV OSS
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-xs md:text-sm font-medium text-muted-foreground">
              *OBS - Priserna är indikationer. Varje badrum är unikt och kräver en fysisk besiktning för exakt offert.
            </p>
            <p className="text-xs md:text-sm font-medium text-muted-foreground mt-1">
              *I priset ingår rivning, tätskikt, plattsättning, el, VVS och slutbesiktning enligt gällande branschregler.
            </p>
          </div>
        </div>

        {}
        <div ref={cardsRef} className={cn("grid md:grid-cols-2 gap-6 md:gap-8 mb-16 transition-all duration-700 delay-200 opacity-0 translate-y-8", isCardsVisible && "opacity-100 translate-y-0")}>
          <Card className="bg-accent/50 border-none shadow-md rounded-lg overflow-hidden">
            <CardHeader className="pb-4 pt-8 px-8">
              <CardTitle asChild className="text-foreground text-xl md:text-2xl font-bold uppercase tracking-wide font-default">
                <h3 className="text-foreground text-xl md:text-2xl font-bold uppercase tracking-wide font-default">
                  PRISPÅVERKANDE FAKTORER
                </h3>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-8 pb-8">
              <ul className="space-y-3">
                {entryReqs.map((req, i) => <li key={req.id} data-index={i} className="flex items-start gap-3 text-foreground/80 text-sm md:text-base font-sans">
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-md bg-primary shrink-0" />
                  <span>{req.text}</span>
                </li>)}
              </ul>
            </CardContent>
          </Card>

          <Card className="bg-accent/50 border-none shadow-md rounded-lg overflow-hidden">
            <CardHeader className="pb-4 pt-8 px-8">
              <CardTitle asChild className="text-foreground text-xl md:text-2xl font-bold uppercase tracking-wide font-default">
                <h3 className="text-foreground text-xl md:text-2xl font-bold uppercase tracking-wide font-default">
                  UNDERLAG FÖR<br />OFFERTFÖRFRÅGAN
                </h3>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-8 pb-8">
              <ul className="space-y-3">
                {docReqs.map((req, i) => <li key={req.id} data-index={i} className="flex items-start gap-3 text-foreground/80 text-sm md:text-base font-sans">
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-md bg-primary shrink-0" />
                  <span>{req.text}</span>
                </li>)}
              </ul>
            </CardContent>
          </Card>
        </div>

        {}
        <div ref={termsRef} className={cn("mb-12 transition-all duration-700 delay-300 opacity-0 translate-y-8", isTermsVisible && "opacity-100 translate-y-0")}>
          <h3 className="bg-foreground text-background py-3 px-6 md:px-8 font-bold text-lg md:text-xl uppercase tracking-wide rounded-t-lg font-default">
            VILLKOR OCH BETALNINGSPROCESS
          </h3>
          <div className="p-6 md:p-8 border border-t-0 border-border bg-background rounded-b-lg shadow-md">
            {Object.entries(groupedTerms).map(([category, items], index) => <div key={category} data-index={index} className="mb-6 last:mb-0">
                <h4 className="font-bold text-foreground mb-3 text-base md:text-lg font-default">{category}</h4>
                <ul className="space-y-2 pl-2">
                  {items.map((item, i) => <li key={item.id} data-index={i} className="flex items-start gap-2 text-muted-foreground text-sm md:text-base font-sans">
                      <ChevronRight className="w-4 h-4 mt-1 text-primary shrink-0" />
                      <span>{item.text}</span>
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>

        {}
        <div className="mt-16">
          <div className="bg-accent text-foreground py-2 px-6 md:px-8 font-bold text-right uppercase tracking-wide rounded-t-lg text-sm md:text-base font-default">CERTIFIERADE PARTNERS FÖR DIN TRYGGHET</div>
          <div className="flex flex-wrap justify-center md:justify-end gap-6 md:gap-10 p-6 md:p-8 border border-t-0 border-border bg-background rounded-b-lg shadow-md items-center">
            {partnersData.map((partner, i) => <div key={partner.id} data-index={i} className="grayscale hover:grayscale-0 transition-all duration-300 opacity-60 hover:opacity-100">
                <img src={partner.src} data-wvc-srcset={`${partner.src}-wvc-srcset`} data-wvc-sizes={`${partner.src}-wvc-sizes`} alt={partner.name} className="h-8 md:h-12 w-auto object-contain" loading="lazy" />
              </div>)}
          </div>
        </div>

      </div>
    </section>;
}